import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Save, User, Lock, Shield, AlertCircle } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const AdminProfile = () => {
  const [profile, setProfile] = useState({
    username: '',
    email: ''
  });
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/auth/profile');
      setProfile(response.data);
      setProfileForm({
        username: response.data.username,
        email: response.data.email || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Ошибка загрузки профиля');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put('/auth/profile', profileForm);
      toast.success('Профиль успешно обновлён');
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.detail || 'Ошибка обновления профиля';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Пароли не совпадают');
      return;
    }

    if (passwordForm.new_password.length < 12) {
      toast.error('Пароль должен содержать минимум 12 символов');
      return;
    }

    setSaving(true);
    
    try {
      await api.put('/auth/profile', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      
      toast.success('Пароль успешно изменён');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.detail || 'Ошибка смены пароля';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление профилем</h1>
          <p className="text-gray-400">Настройки учётной записи администратора</p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 bg-gray-800">
          <TabsTrigger value="profile" className="data-[state=active]:bg-blue-600">
            <User className="mr-2 h-4 w-4" />
            Профиль
          </TabsTrigger>
          <TabsTrigger value="password" className="data-[state=active]:bg-blue-600">
            <Lock className="mr-2 h-4 w-4" />
            Пароль
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <User className="mr-2 h-5 w-5" />
                Основная информация
              </CardTitle>
              <CardDescription className="text-gray-400">
                Обновите свою основную информацию
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-gray-300">Имя пользователя</Label>
                    <Input
                      id="username"
                      value={profileForm.username}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, username: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Введите имя пользователя"
                      required
                      data-testid="profile-username-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">Email адрес</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="admin@example.com"
                      data-testid="profile-email-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-profile-btn"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Сохранение...' : 'Сохранить профиль'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="password" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Lock className="mr-2 h-5 w-5" />
                Смена пароля
              </CardTitle>
              <CardDescription className="text-gray-400">
                Обновите пароль для входа в систему
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_password" className="text-gray-300">Текущий пароль</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Введите текущий пароль"
                      required
                      data-testid="current-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new_password" className="text-gray-300">Новый пароль</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Введите новый пароль"
                      required
                      minLength="6"
                      data-testid="new-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password" className="text-gray-300">Подтвердите новый пароль</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Повторите новый пароль"
                      required
                      minLength="6"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Alert className="border-yellow-600 bg-yellow-600/10">
                  <AlertCircle className="h-4 w-4 text-yellow-400" />
                  <AlertDescription className="text-yellow-400">
                    Пароль должен содержать минимум 6 символов. После смены пароля потребуется повторный вход.
                  </AlertDescription>
                </Alert>

                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-password-btn"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    {saving ? 'Сохранение...' : 'Изменить пароль'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default AdminProfile;
